import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, CircleCheck as CheckCircle2, Clock, CircleAlert as AlertCircle } from 'lucide-react';
import type { MissionControlCycle } from '../../types';
import type { EnrichedCycle } from '../../pages/app/MissionControl';

interface Props {
  name: string;
  email: string;
  cycles: MissionControlCycle[];
  currentCycle: EnrichedCycle | null;
  onClick: () => void;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  draft: { label: 'Draft', color: 'text-app-muted', bg: 'bg-app-canvas', icon: Clock },
  submitted: { label: 'Awaiting Approval', color: 'text-app-focus', bg: 'bg-app-focus/10', icon: Clock },
  approved: { label: 'Approved', color: 'text-forest-700', bg: 'bg-forest-50', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: 'text-app-warning', bg: 'bg-app-warning/10', icon: Clock },
  completed: { label: 'Completed', color: 'text-forest-700', bg: 'bg-forest-50', icon: CheckCircle2 },
  reviewed: { label: 'Reviewed', color: 'text-app-muted', bg: 'bg-app-canvas', icon: CheckCircle2 },
};

function computeRollingScore(cycles: MissionControlCycle[]): number {
  const scored = cycles.filter(c => (c.status === 'reviewed' || c.status === 'completed') && c.completion_score > 0);
  if (scored.length === 0) return 0;

  // Exponential decay: most recent cycle gets highest weight
  const sorted = [...scored].sort((a, b) => new Date(b.cycle_end).getTime() - new Date(a.cycle_end).getTime());
  const decay = 0.7;
  let weightedSum = 0;
  let weightTotal = 0;
  for (let i = 0; i < sorted.length; i++) {
    const w = Math.pow(decay, i);
    weightedSum += sorted[i].completion_score * w;
    weightTotal += w;
  }
  return weightTotal > 0 ? weightedSum / weightTotal : 0;
}

function computeTrend(cycles: MissionControlCycle[]): 'up' | 'down' | 'flat' {
  const scored = cycles
    .filter(c => (c.status === 'reviewed' || c.status === 'completed') && c.completion_score > 0)
    .sort((a, b) => new Date(b.cycle_end).getTime() - new Date(a.cycle_end).getTime());
  if (scored.length < 2) return 'flat';
  const recent = scored[0].completion_score;
  const prev = scored[1].completion_score;
  const diff = recent - prev;
  if (Math.abs(diff) < 5) return 'flat';
  return diff > 0 ? 'up' : 'down';
}

export default function TeamMemberCard({ name, email, cycles, currentCycle, onClick }: Props) {
  const rollingScore = useMemo(() => computeRollingScore(cycles), [cycles]);
  const trend = useMemo(() => computeTrend(cycles), [cycles]);
  const completedCycles = useMemo(() => cycles.filter(c => c.status === 'reviewed' || c.status === 'completed').length, [cycles]);

  const currentStats = useMemo(() => {
    if (!currentCycle) return null;
    const totalObj = currentCycle.objectives.length;
    const completedObj = currentCycle.objectives.filter(o => o.status === 'completed').length;
    const totalContacts = currentCycle.objectives.reduce((s, o) => s + o.contacts.length, 0);
    const completedContacts = currentCycle.objectives.reduce((s, o) => s + o.contacts.filter(c => c.status === 'completed').length, 0);
    return { totalObj, completedObj, totalContacts, completedContacts };
  }, [currentCycle]);

  const status = currentCycle?.status || 'none';
  const statusInfo = STATUS_LABELS[status];
  const progressPct = currentStats && currentStats.totalContacts > 0
    ? (currentStats.completedContacts / currentStats.totalContacts) * 100
    : 0;

  const initials = name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const scoreColor = rollingScore >= 80 ? 'text-forest-600' : rollingScore >= 50 ? 'text-app-warning' : 'text-app-critical';

  return (
    <div
      onClick={onClick}
      className="bg-app-surface rounded-lg border border-app-line p-5 hover:shadow-none hover:border-forest-200 transition cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-forest-50 flex items-center justify-center text-sm font-bold text-forest-700 group-hover:bg-forest-100 transition-colors">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-app-ink truncate">{name}</p>
          <p className="text-[10px] text-app-muted truncate">{email}</p>
        </div>
        {trend === 'up' && <TrendingUp className="w-4 h-4 text-forest-500" />}
        {trend === 'down' && <TrendingDown className="w-4 h-4 text-app-critical" />}
        {trend === 'flat' && <Minus className="w-4 h-4 text-app-muted" />}
      </div>

      {/* Current cycle status */}
      {currentCycle && statusInfo && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
              <statusInfo.icon className="w-3 h-3" />
              {statusInfo.label}
            </span>
            {currentStats && (
              <span className="text-[10px] text-app-muted">
                {currentStats.completedObj}/{currentStats.totalObj} accounts
              </span>
            )}
          </div>
          <div className="h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${name} objective progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressPct)}>
            <div
              className="h-full rounded-full bg-app-accent transition duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {currentStats && (
            <p className="text-[10px] text-app-muted mt-1">
              {currentStats.completedContacts}/{currentStats.totalContacts} contacts targeted
            </p>
          )}
        </div>
      )}

      {!currentCycle && (
        <div className="mb-3 flex items-center gap-2 text-xs text-app-muted">
          <AlertCircle className="w-3.5 h-3.5" />
          No active objectives
        </div>
      )}

      {/* Rolling score */}
      <div className="flex items-center justify-between pt-3 border-t border-app-line">
        <div>
          <p className="text-[10px] text-app-muted">Rolling Score</p>
          <p className={`text-lg font-bold ${scoreColor}`}>
            {rollingScore > 0 ? `${Math.round(rollingScore)}%` : 'N/A'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-app-muted">Cycles</p>
          <p className="text-sm font-semibold text-app-ink">{completedCycles}</p>
        </div>

        {/* Mini sparkline from last 6 cycles */}
        {completedCycles >= 2 && (
          <div className="flex items-end gap-0.5 h-6">
            {cycles
              .filter(c => c.status === 'reviewed' || c.status === 'completed')
              .sort((a, b) => new Date(a.cycle_end).getTime() - new Date(b.cycle_end).getTime())
              .slice(-6)
              .map((c, i) => (
                <div
                  key={i}
                  className={`w-1.5 rounded-full transition ${
                    c.completion_score >= 80 ? 'bg-forest-400' : c.completion_score >= 50 ? 'bg-app-warning' : 'bg-app-critical'
                  }`}
                  style={{ height: `${Math.max(4, (c.completion_score / 100) * 24)}px` }}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
